import Image from '@/components/Image';

const LOGO = {
  khan: '/assets/site-logos/khan-academy.svg',
  ixl: '/assets/site-logos/ixl.svg',
  phet: '/assets/site-logos/phet.svg',
  scratch: '/assets/site-logos/scratch.svg',
} as const;

interface ChatMessage {
  role: 'pengi' | 'student';
  text: string;
}

interface SiteScene {
  name: string;
  url: string;
  color: string;
  logo: string;
  pageContent: React.ReactNode;
  chat: ChatMessage[];
}

const SITES: Record<string, SiteScene> = {
  khan: {
    name: 'Khan Academy',
    url: 'khanacademy.org/math/algebra/...',
    color: '#1865F2',
    logo: LOGO.khan,
    pageContent: (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="h-5 w-5 rounded bg-[#1865F2]/20" />
          <div className="h-3 w-40 rounded-full bg-[#1865F2]/15" />
        </div>
        <div className="h-3 w-3/4 rounded-full bg-[#E3E8F2]" />
        <div className="h-3 w-1/2 rounded-full bg-[#E3E8F2]" />
        <div className="mt-4 flex gap-3">
          <div className="h-24 flex-1 rounded-xl bg-[#F4F7FB] p-3">
            <div className="h-2 w-12 rounded-full bg-[#1865F2]/20" />
            <div className="mt-3 h-10 w-full rounded-lg bg-[#1865F2]/10" />
          </div>
          <div className="hidden h-24 flex-1 rounded-xl bg-[#F4F7FB] sm:block" />
        </div>
        <div className="h-3 w-2/3 rounded-full bg-[#E3E8F2]" />
        <div className="h-3 w-5/6 rounded-full bg-[#E3E8F2]" />
      </div>
    ),
    chat: [
      { role: 'pengi', text: 'What do you think happens when x increases in this equation?' },
      { role: 'student', text: 'I think y goes up too?' },
      { role: 'pengi', text: "Great intuition! Let's verify with the graph..." },
    ],
  },
  ixl: {
    name: 'IXL',
    url: 'ixl.com/math/grade-5/add-fractions',
    color: '#00B050',
    logo: LOGO.ixl,
    pageContent: (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="h-5 w-5 rounded bg-[#00B050]/20" />
          <div className="h-3 w-48 rounded-full bg-[#00B050]/15" />
        </div>
        <div className="mt-2 rounded-xl bg-[#F4F7FB] p-4">
          <div className="h-2 w-20 rounded-full bg-[#8A96A8]/30" />
          <div className="mt-3 flex items-center gap-2">
            <div className="flex h-8 w-12 items-center justify-center rounded-lg bg-[#00B050]/15">
              <span className="text-[10px] text-[#00B050]/60">3/4</span>
            </div>
            <span className="text-[10px] text-[#8A96A8]">+</span>
            <div className="flex h-8 w-12 items-center justify-center rounded-lg bg-[#00B050]/15">
              <span className="text-[10px] text-[#00B050]/60">1/2</span>
            </div>
            <span className="text-[10px] text-[#8A96A8]">=</span>
            <div className="h-8 w-16 rounded-lg border border-dashed border-[#8A96A8]/30" />
          </div>
        </div>
        <div className="h-3 w-2/3 rounded-full bg-[#E3E8F2]" />
        <div className="flex gap-2">
          <div className="h-8 w-20 rounded-lg bg-[#E3E8F2]" />
          <div className="h-8 w-20 rounded-lg bg-[#E3E8F2]" />
        </div>
      </div>
    ),
    chat: [
      { role: 'pengi', text: 'To add 3/4 and 1/2, what do we need to find first?' },
      { role: 'student', text: 'A common denominator?' },
      { role: 'pengi', text: 'Exactly! What number works for both 4 and 2?' },
    ],
  },
  phet: {
    name: 'PhET Simulations',
    url: 'phet.colorado.edu/sims/gravity-force',
    color: '#E8740C',
    logo: LOGO.phet,
    pageContent: (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="h-5 w-5 rounded bg-[#E8740C]/20" />
          <div className="h-3 w-36 rounded-full bg-[#E8740C]/15" />
        </div>
        <div className="mt-2 rounded-xl bg-gradient-to-br from-[#1a1a2e] to-[#16213e] p-4">
          <div className="flex items-center justify-center gap-6">
            <div className="h-8 w-8 rounded-full bg-[#E8740C]/60" />
            <div className="flex flex-col items-center gap-1">
              <div className="h-0.5 w-16 bg-white/20" />
              <span className="text-[8px] text-white/40">Force</span>
            </div>
            <div className="h-5 w-5 rounded-full bg-[#4ECDC4]/60" />
          </div>
          <div className="mt-2 flex justify-center gap-4">
            <div className="h-4 w-16 rounded bg-white/10" />
            <div className="h-4 w-16 rounded bg-white/10" />
          </div>
        </div>
        <div className="h-3 w-1/2 rounded-full bg-[#E3E8F2]" />
      </div>
    ),
    chat: [
      { role: 'pengi', text: 'What happens to the force when you move the objects closer together?' },
      { role: 'student', text: 'The force gets bigger!' },
      { role: 'pengi', text: "Right! That's the inverse-square law in action." },
    ],
  },
  scratch: {
    name: 'Scratch',
    url: 'scratch.mit.edu/projects/editor',
    color: '#FF8C1A',
    logo: LOGO.scratch,
    pageContent: (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="h-5 w-5 rounded bg-[#FF8C1A]/20" />
          <div className="h-3 w-32 rounded-full bg-[#FF8C1A]/15" />
        </div>
        <div className="mt-2 flex gap-2">
          <div className="flex-1 space-y-1.5 rounded-xl bg-[#F4F7FB] p-3">
            <div className="h-5 w-24 rounded bg-[#4C97FF]/40" />
            <div className="ml-3 h-5 w-28 rounded bg-[#9966FF]/40" />
            <div className="ml-3 h-5 w-20 rounded bg-[#CF63CF]/40" />
            <div className="h-5 w-24 rounded bg-[#FFBF00]/40" />
          </div>
          <div className="hidden w-24 items-center justify-center rounded-xl border border-[#E3E8F2] bg-white sm:flex">
            <div className="h-6 w-6 rounded bg-[#FF8C1A]/30" />
          </div>
        </div>
        <div className="h-3 w-1/2 rounded-full bg-[#E3E8F2]" />
      </div>
    ),
    chat: [
      { role: 'pengi', text: "Your sprite isn't moving. Which block controls movement?" },
      { role: 'student', text: 'The move 10 steps block?' },
      { role: 'pengi', text: 'Yes! Now put it inside your forever loop.' },
    ],
  },
};

export type SiteKey = keyof typeof SITES;

interface StaticBrowserMockupProps {
  sites: SiteKey[];
}

export default function StaticBrowserMockup({ sites: siteKeys }: StaticBrowserMockupProps) {
  const scenes = siteKeys.map((key) => SITES[key]).filter(Boolean);
  if (scenes.length === 0) return null;

  const primary = scenes[0];

  return (
    <div className="relative mx-auto max-w-[1000px]">
      <div className="overflow-hidden rounded-2xl border border-[#D8E3EF] bg-white shadow-[0_8px_48px_rgba(31,42,68,0.1)]">
        {/* browser chrome */}
        <div className="flex items-center gap-2 border-b border-[#E3E8F2] bg-[#F4F7FB] px-4 py-3">
          <div className="flex gap-1.5">
            <span className="h-3 w-3 rounded-full bg-[#FF5F57]" />
            <span className="h-3 w-3 rounded-full bg-[#FFBD2E]" />
            <span className="h-3 w-3 rounded-full bg-[#28C840]" />
          </div>
          <div className="ml-3 flex flex-1 items-center gap-2 overflow-hidden rounded-lg bg-white px-3 py-1.5 text-sm text-[#8A96A8]">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={primary.logo} alt="" className="h-4 w-4 shrink-0 rounded-sm" />
            <span className="display-text truncate">{primary.url}</span>
          </div>
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#42C52D]/10">
            <Image src="/assets/landing-logo.png" alt="Pengi" width={18} height={18} optimizedWidth={36} />
          </div>
        </div>

        {/* page content */}
        <div className="relative flex min-h-[280px] items-stretch gap-6 p-6 md:min-h-[360px] md:p-10">
          <div className="min-w-0 flex-1">{primary.pageContent}</div>
          <div className="w-[240px] shrink-0 rounded-2xl border border-[#C5EDB8] bg-white p-4 shadow-[0_4px_24px_rgba(66,197,45,0.12)] md:w-[290px] md:p-5">
            <div className="mb-3 flex items-center gap-2">
              <Image src="/assets/landing-logo.png" alt="Pengi" width={24} height={24} optimizedWidth={48} />
              <span className="emphasize-text text-sm text-[#1F2A44]">Pengi Live Tutor</span>
            </div>
            <div className="space-y-2.5">
              {primary.chat.map((msg, i) =>
                msg.role === 'pengi' ? (
                  <div key={i} className="rounded-xl rounded-tl-sm bg-[#F0FBE8] px-3.5 py-2.5">
                    <p className="display-text text-xs leading-relaxed text-[#277A1C]">{msg.text}</p>
                  </div>
                ) : (
                  <div key={i} className="ml-auto w-fit rounded-xl rounded-tr-sm bg-[#F4F7FB] px-3.5 py-2.5">
                    <p className="display-text text-xs leading-relaxed text-[#4A5A72]">{msg.text}</p>
                  </div>
                ),
              )}
            </div>
          </div>
        </div>
      </div>

      {/* site labels for multi-platform pages */}
      {scenes.length > 1 && (
        <div className="mt-5 flex justify-center gap-2">
          {scenes.map((s) => (
            <div
              key={s.name}
              className="inline-flex items-center gap-2 rounded-full border border-[#E3E8F2] bg-white px-4 py-2 text-sm shadow-sm"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={s.logo} alt={s.name} className="h-5 w-5 rounded-sm object-contain" />
              <span className="display-text-semi text-[#1F2A44]">{s.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
